// services/legacyExchange.ts
import { getAuthUrl } from './authClient';
import { addSentryBreadcrumb } from '../utils/sentry';

/**
 * One-time bridge from the legacy JWT pair to a Better Auth session cookie
 * (ORA-987). A browser that still holds the old tokens trades them once, on
 * boot, so deploying the cookie-session frontend does not sign anyone out.
 */

/** localStorage keys the legacy frontend wrote. All removed after a trade. */
export const LEGACY_AUTH_STORAGE_KEYS = [
  'authToken',
  'refreshToken',
  'userId',
  'originalAdminSession',
] as const;

export const LEGACY_EXCHANGE_PATH = '/legacy/exchange';

/** Statuses that mean the backend looked at the token and refused it. A
 * 403 counts only with this code: other 403s (Better Auth's origin check)
 * say nothing about the token. */
// 404: the backend retired the exchange route (ORA-988, 2026-10-27), so the
// tokens can never be traded again; drop them instead of retrying each boot.
const REFUSED_TOKEN_STATUSES = new Set([400, 401, 404]);
export const TWO_FACTOR_REQUIRED_CODE = 'TWO_FACTOR_REQUIRED';

const isTokenRefusal = async (response: Response): Promise<boolean> => {
  if (REFUSED_TOKEN_STATUSES.has(response.status)) return true;
  if (response.status !== 403) return false;

  const body = (await response.json().catch(() => null)) as {
    code?: unknown;
  } | null;

  return body?.code === TWO_FACTOR_REQUIRED_CODE;
};

/** Long enough for a cold backend, short enough not to strand the boot. */
export const LEGACY_EXCHANGE_TIMEOUT_MS = 10_000;

export type LegacyExchangeOutcome =
  /** Nothing to trade: no legacy token in storage. */
  | 'none'
  /** Traded: the session cookie is set, the legacy tokens are gone. */
  | 'exchanged'
  /** The backend refused the token; it is useless, so it was dropped. */
  | 'rejected'
  /** Network failure, timeout, 429 or 5xx: tokens kept, retried next boot. */
  | 'deferred';

const readString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() && value !== 'null'
    ? value.trim()
    : null;

const parseJson = (value: string | null): unknown => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

/**
 * The legacy token to trade, or null. An admin who was mid-impersonation when
 * the new frontend shipped trades their own parked token, not the student's,
 * so they land back in their own account rather than stuck as the student
 * with no way to stop. Falls back to the copy inside redux-persist's
 * `persist:root` for a browser whose separate keys were lost.
 */
export const readLegacyToken = (): string | null => {
  const parked = parseJson(localStorage.getItem('originalAdminSession')) as {
    refreshToken?: unknown;
    authToken?: unknown;
  } | null;

  const parkedToken =
    readString(parked?.refreshToken) ?? readString(parked?.authToken);

  if (parkedToken) return parkedToken;

  const storedToken =
    readString(localStorage.getItem('refreshToken')) ??
    readString(localStorage.getItem('authToken'));

  if (storedToken) return storedToken;

  const persistedRoot = parseJson(localStorage.getItem('persist:root')) as {
    auth?: unknown;
  } | null;

  const persistedAuth = parseJson(
    typeof persistedRoot?.auth === 'string' ? persistedRoot.auth : null
  ) as { refreshToken?: unknown; authToken?: unknown } | null;

  return (
    readString(persistedAuth?.refreshToken) ??
    readString(persistedAuth?.authToken)
  );
};

/** Drops every legacy token copy (the separate keys; the persisted slice is
 * stripped by the persist migration in `store.ts`). */
export const clearLegacyAuthStorage = (): void => {
  LEGACY_AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
};

/**
 * Runs the trade. Never throws: every ending is an outcome, and the caller's
 * normal session check (the profile fetch) decides who is signed in.
 */
export const exchangeLegacyTokens = async (
  fetchImpl: typeof fetch = (...args) => fetch(...args)
): Promise<LegacyExchangeOutcome> => {
  let token: string | null;
  try {
    token = readLegacyToken();
  } catch {
    return 'none';
  }
  if (!token) return 'none';

  const controller = new AbortController();

  const timeout = setTimeout(
    () => controller.abort(),
    LEGACY_EXCHANGE_TIMEOUT_MS
  );

  try {
    const response = await fetchImpl(getAuthUrl(LEGACY_EXCHANGE_PATH), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: token }),
      signal: controller.signal,
    });

    if (response.ok) {
      clearLegacyAuthStorage();

      addSentryBreadcrumb({
        category: 'auth-legacy-exchange',
        level: 'info',
        message: 'Legacy tokens exchanged for a session',
      });

      return 'exchanged';
    }

    // 401: the token is expired or revoked. 403 TWO_FACTOR_REQUIRED: the
    // account has since enabled 2FA, so only a fresh sign-in will do. 400:
    // a malformed token. 404: the route is gone for good. Anything else
    // (429, 5xx) says nothing about the token: keep it and retry on the
    // next boot.
    if (await isTokenRefusal(response)) {
      clearLegacyAuthStorage();

      addSentryBreadcrumb({
        category: 'auth-legacy-exchange',
        level: 'warning',
        message: 'Legacy token rejected',
        data: { status: response.status },
      });

      return 'rejected';
    }

    return 'deferred';
  } catch {
    return 'deferred';
  } finally {
    clearTimeout(timeout);
  }
};

let bootExchange: Promise<LegacyExchangeOutcome> | null = null;
let settledOutcome: LegacyExchangeOutcome | null = null;

/**
 * True when this boot could not trade legacy tokens it still holds (network,
 * 404, 429, 5xx). A 401 then is the missing cookie, not a lost session: the
 * tab must not sign out (that would delete the tokens the next boot retries
 * with).
 */
export const isLegacyExchangePending = (): boolean =>
  settledOutcome === 'deferred';

/**
 * The boot's single exchange. Every caller (the app root, the axios request
 * interceptor, which can run first from a route loader) shares one attempt,
 * so nothing reaches the API before a legacy session has become a cookie.
 */
export const runLegacyExchangeOnce = (): Promise<LegacyExchangeOutcome> => {
  if (!bootExchange) {
    bootExchange = exchangeLegacyTokens().then((outcome) => {
      settledOutcome = outcome;
      return outcome;
    });
  }
  return bootExchange;
};

/** Test-only: forget this boot's exchange. */
export const resetLegacyExchangeForTests = (): void => {
  bootExchange = null;
  settledOutcome = null;
};
